import React from "react"
import { Eye, MousePointerClick, DollarSign, TrendingUp } from "lucide-react"

interface MetricsSummary {
  totalImpressions: number
  totalClicks: number
  totalSpend: number
  totalConversions: number
  avgCtr: number
  avgCpc: number
}

interface MetricsCardsProps {
  data?: {
    summary: MetricsSummary
  } | null
  isLoading: boolean
}

export function MetricsCards({ data, isLoading }: MetricsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-card rounded-lg border border-border p-6">
            <div className="animate-pulse">
              <div className="w-8 h-8 bg-muted rounded-lg mb-4"></div>
              <div className="h-4 bg-muted rounded mb-2"></div>
              <div className="h-6 bg-muted rounded mb-1"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!data?.summary) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-card rounded-lg border border-border p-6">
            <div className="text-center text-muted-foreground">
              <div className="w-8 h-8 bg-muted rounded-lg mx-auto mb-4"></div>
              <p className="text-sm">No data available</p>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const { summary } = data

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M"
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K"
    }
    return num.toLocaleString()
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatPercentage = (rate: number) => {
    return (rate * 100).toFixed(2) + "%"
  }

  const metrics = [
    {
      label: "Impressions",
      value: formatNumber(summary.totalImpressions),
      subValue: "Total reach",
      icon: Eye,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Clicks",
      value: formatNumber(summary.totalClicks),
      subValue: `${formatPercentage(summary.avgCtr)} CTR`,
      icon: MousePointerClick,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "Spend",
      value: formatCurrency(summary.totalSpend),
      subValue: `${formatCurrency(summary.avgCpc)} avg CPC`,
      icon: DollarSign,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
    },
    {
      label: "Conversions",
      value: formatNumber(summary.totalConversions),
      subValue:
        summary.totalClicks > 0
          ? `${((summary.totalConversions / summary.totalClicks) * 100).toFixed(2)}% rate`
          : "0% rate",
      icon: TrendingUp,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {metrics.map((metric) => (
        <div key={metric.label} className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-10 h-10 ${metric.bgColor} rounded-lg flex items-center justify-center`}>
              <metric.icon className={`w-5 h-5 ${metric.color}`} />
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-1">{metric.label}</p>
            <p className="text-2xl font-bold text-card-foreground mb-1">{metric.value}</p>
            <p className="text-xs text-muted-foreground">{metric.subValue}</p>
          </div>
        </div>
      ))}
    </div>
  )
}
